import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
  LogOut,
  Anchor,
  FileText,
  Users,
  MessageSquare,
  TrendingUp,
  Trash2,
  ExternalLink,
  Building2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import AdminSubsidiaries from "@/pages/admin/AdminSubsidiaries";

const RFQ_STATUS = ["new", "in_review", "responded", "closed"];
const CAREER_STATUS = ["new", "shortlisted", "interviewing", "hired", "rejected"];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("rfq");
  const [stats, setStats] = useState(null);
  const [rfqs, setRfqs] = useState([]);
  const [careers, setCareers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);

  const loadAll = async () => {
    try {
      const [s, r, c, m] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/rfqs"),
        api.get("/admin/careers"),
        api.get("/admin/messages"),
      ]);
      setStats(s.data);
      setRfqs(r.data);
      setCareers(c.data);
      setMessages(m.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    if (user?.role === "admin") loadAll();
  }, [user]);

  if (loading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/admin" replace />;

  const updateRfqStatus = async (id, status) => {
    try {
      await api.patch(`/admin/rfqs/${id}`, { status });
      setRfqs((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("RFQ status updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const updateCareerStatus = async (id, status) => {
    try {
      await api.patch(`/admin/careers/${id}`, { status });
      setCareers((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("Application status updated.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const deleteRfq = async (id) => {
    if (!window.confirm("Delete this RFQ?")) return;
    try {
      await api.delete(`/admin/rfqs/${id}`);
      setRfqs((xs) => xs.filter((x) => x.id !== id));
      toast.success("RFQ deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const deleteCareer = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await api.delete(`/admin/careers/${id}`);
      setCareers((xs) => xs.filter((x) => x.id !== id));
      toast.success("Application deleted.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#060A14] text-white flex">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/10 bg-[#0C1322] flex-shrink-0 flex flex-col">
        <div className="p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#FF5722] flex items-center justify-center">
              <Anchor size={18} className="text-white" strokeWidth={2.5} />
            </div>
            <div className="leading-none">
              <div className="font-display font-black text-[15px]">BILLTECH HOLDINGS</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 mt-[2px]">Admin</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 p-3">
          <SidebarBtn active={tab === "rfq"} onClick={() => setTab("rfq")} icon={<FileText size={16} />} label="RFQs" count={stats?.rfq_new} testId={ADMIN.tabRfq} />
          <SidebarBtn active={tab === "careers"} onClick={() => setTab("careers")} icon={<Users size={16} />} label="Careers" count={stats?.career_new} testId={ADMIN.tabCareers} />
          <SidebarBtn active={tab === "messages"} onClick={() => setTab("messages")} icon={<MessageSquare size={16} />} label="Messages" testId={ADMIN.tabMessages} />
          <SidebarBtn active={tab === "subs"} onClick={() => setTab("subs")} icon={<Building2 size={16} />} label="Subsidiaries" testId={ADMIN.tabSubs} />
        </nav>

        <div className="p-3 border-t border-white/10 space-y-2">
          <div className="p-3 text-xs text-white/60">
            Signed in as <br /><span className="text-white">{user.email}</span>
          </div>
          <button
            onClick={logout}
            data-testid={ADMIN.logout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm border border-white/10 hover:border-[#FF5722] hover:text-[#FF5722] transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-white/10 bg-[#0C1322] px-8 py-6">
          <div className="eyebrow mb-1">Command Portal</div>
          <h1 className="font-display text-2xl tracking-tight">
            {tab === "rfq"
              ? "Project RFQs"
              : tab === "careers"
              ? "Career Applications"
              : tab === "messages"
              ? "Contact Messages"
              : "Subsidiary Registry"}
          </h1>
        </header>

        <div className="p-8 flex-1 overflow-auto">
          {stats && tab !== "subs" && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <StatCard label="Total RFQs" value={stats.rfq_total} icon={<FileText size={16} />} />
              <StatCard label="New RFQs" value={stats.rfq_new} icon={<TrendingUp size={16} />} accent />
              <StatCard label="Applications" value={stats.career_total} icon={<Users size={16} />} />
              <StatCard label="New Applicants" value={stats.career_new} icon={<TrendingUp size={16} />} accent />
            </div>
          )}

          {tab === "rfq" && (
            <RfqTable rfqs={rfqs} onStatus={updateRfqStatus} onDelete={deleteRfq} onOpen={setSelected} />
          )}
          {tab === "careers" && (
            <CareersTable rows={careers} onStatus={updateCareerStatus} onDelete={deleteCareer} onOpen={setSelected} />
          )}
          {tab === "messages" && <MessagesTable rows={messages} onOpen={setSelected} />}
          {tab === "subs" && <AdminSubsidiaries />}
        </div>
      </main>

      {selected && <DetailDrawer item={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function SidebarBtn({ active, onClick, icon, label, count, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm border-l-2 transition-colors ${
        active ? "border-[#FF5722] bg-white/[0.03] text-white" : "border-transparent text-white/70 hover:text-white hover:bg-white/[0.02]"
      }`}
    >
      {icon}
      <span className="flex-1 text-left">{label}</span>
      {count > 0 && (
        <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[#FF5722] text-white">
          {count}
        </span>
      )}
    </button>
  );
}

function StatCard({ label, value, icon, accent }) {
  return (
    <div className={`border p-5 ${accent ? "border-[#FF5722] bg-[#FF5722]/5" : "border-white/10 bg-[#0C1322]"}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/60">{label}</div>
        <div className={accent ? "text-[#FF5722]" : "text-white/40"}>{icon}</div>
      </div>
      <div className="font-display text-3xl font-black">{value ?? "—"}</div>
    </div>
  );
}

function RfqTable({ rfqs, onStatus, onDelete, onOpen }) {
  if (!rfqs.length) return <Empty label="No RFQs yet." />;
  return (
    <div className="border border-white/10 bg-[#0C1322] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-white/10">
          <tr className="text-left">
            {["Company", "Contact", "Region", "Divisions", "Submitted", "Status", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 font-normal">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rfqs.map((r) => (
            <tr key={r.id} data-testid={ADMIN.rfqRow(r.id)} className="hover:bg-white/[0.02]">
              <td className="px-4 py-3">
                <button onClick={() => onOpen({ type: "rfq", ...r })} className="font-display font-bold text-white hover:text-[#FF5722] text-left">
                  {r.company_name}
                </button>
              </td>
              <td className="px-4 py-3 text-white/70">
                <div>{r.contact_name}</div>
                <div className="text-[11px] text-white/50">{r.contact_email}</div>
              </td>
              <td className="px-4 py-3 text-white/70">{r.maritime_region}</td>
              <td className="px-4 py-3 text-white/70 text-[11px]">{(r.divisions || []).length}</td>
              <td className="px-4 py-3 text-white/60 text-[11px] font-mono">
                {new Date(r.created_at).toLocaleDateString()}
              </td>
              <td className="px-4 py-3">
                <select
                  value={r.status}
                  onChange={(e) => onStatus(r.id, e.target.value)}
                  data-testid={ADMIN.rfqStatusSelect(r.id)}
                  className="bg-[#060A14] border border-white/10 px-2 py-1 text-xs focus:border-[#FF5722] outline-none"
                >
                  {RFQ_STATUS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => onOpen({ type: "rfq", ...r })} className="text-white/50 hover:text-white" aria-label="Open">
                    <ExternalLink size={14} />
                  </button>
                  <button onClick={() => onDelete(r.id)} data-testid={ADMIN.rfqDeleteBtn(r.id)} className="text-white/40 hover:text-[#EF4444]" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CareersTable({ rows, onStatus, onDelete, onOpen }) {
  if (!rows.length) return <Empty label="No applications yet." />;
  return (
    <div className="border border-white/10 bg-[#0C1322] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-white/10">
          <tr className="text-left">
            {["Name", "Position", "Experience", "Division Pref", "Submitted", "Status", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 font-normal">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((r) => (
            <tr key={r.id} data-testid={ADMIN.careerRow(r.id)} className="hover:bg-white/[0.02]">
              <td className="px-4 py-3">
                <button onClick={() => onOpen({ type: "career", ...r })} className="font-display font-bold text-white hover:text-[#FF5722] text-left">
                  {r.full_name}
                </button>
                <div className="text-[11px] text-white/50">{r.email}</div>
              </td>
              <td className="px-4 py-3 text-white/70">{r.position}</td>
              <td className="px-4 py-3 text-white/70">{r.years_experience} yrs</td>
              <td className="px-4 py-3 text-white/60 text-[11px]">{r.division_preference || "—"}</td>
              <td className="px-4 py-3 text-white/60 text-[11px] font-mono">
                {new Date(r.created_at).toLocaleDateString()}
              </td>
              <td className="px-4 py-3">
                <select
                  value={r.status}
                  onChange={(e) => onStatus(r.id, e.target.value)}
                  data-testid={ADMIN.careerStatusSelect(r.id)}
                  className="bg-[#060A14] border border-white/10 px-2 py-1 text-xs focus:border-[#FF5722] outline-none"
                >
                  {CAREER_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <button onClick={() => onOpen({ type: "career", ...r })} className="text-white/50 hover:text-white" aria-label="Open">
                    <ExternalLink size={14} />
                  </button>
                  <button onClick={() => onDelete(r.id)} data-testid={ADMIN.careerDeleteBtn(r.id)} className="text-white/40 hover:text-[#EF4444]" aria-label="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MessagesTable({ rows, onOpen }) {
  if (!rows.length) return <Empty label="No messages yet." />;
  return (
    <div className="border border-white/10 bg-[#0C1322] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-white/10">
          <tr className="text-left">
            {["Name", "Email", "Subject", "Received"].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-[0.2em] text-white/50 font-normal">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-white/[0.02]">
              <td className="px-4 py-3 font-display font-bold">
                <button onClick={() => onOpen({ type: "msg", ...r })} className="hover:text-[#FF5722]">{r.name}</button>
              </td>
              <td className="px-4 py-3 text-white/70">{r.email}</td>
              <td className="px-4 py-3 text-white/70">{r.subject}</td>
              <td className="px-4 py-3 text-white/60 text-[11px] font-mono">{new Date(r.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="border border-white/10 bg-[#0C1322] p-16 text-center">
      <div className="font-display text-2xl mb-1">{label}</div>
      <div className="text-white/60 text-sm">New submissions will appear here.</div>
    </div>
  );
}

function DetailDrawer({ item, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex" onClick={onClose}>
      <div className="flex-1 bg-black/60" />
      <div
        className="w-full max-w-xl bg-[#0C1322] border-l border-white/10 h-full overflow-y-auto p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="eyebrow">{item.type === "rfq" ? "Project RFQ" : item.type === "career" ? "Career Application" : "Contact Message"}</div>
            <h3 className="font-display text-2xl mt-2 tracking-tight">
              {item.company_name || item.full_name || item.name}
            </h3>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="admin-drawer-close">×</button>
        </div>

        <div className="space-y-4 text-sm">
          {item.type === "rfq" && (
            <>
              <Row k="Contact" v={`${item.contact_name} · ${item.contact_email}${item.contact_phone ? " · " + item.contact_phone : ""}`} />
              <Row k="Region" v={item.maritime_region} />
              <Row k="Divisions" v={(item.divisions || []).join(", ") || "—"} />
              <Row k="Target Start" v={item.target_start_date || "—"} />
              <Row k="Status" v={item.status} />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Project Scope</div>
                <div className="bg-[#060A14] border border-white/10 p-4 text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
                  {item.project_scope}
                </div>
              </div>
            </>
          )}
          {item.type === "career" && (
            <>
              <Row k="Email" v={item.email} />
              <Row k="Phone" v={item.phone || "—"} />
              <Row k="Position" v={item.position} />
              <Row k="Experience" v={`${item.years_experience} years`} />
              <Row k="Division Pref" v={item.division_preference || "—"} />
              <Row k="Certifications" v={item.certifications || "—"} />
              <Row k="Resume" v={item.resume_url ? <a href={item.resume_url} target="_blank" rel="noreferrer" className="text-[#FF5722] hover:underline">Open link</a> : "—"} />
              <Row k="Status" v={item.status} />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Cover Letter</div>
                <div className="bg-[#060A14] border border-white/10 p-4 text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
                  {item.cover_letter}
                </div>
              </div>
            </>
          )}
          {item.type === "msg" && (
            <>
              <Row k="Email" v={item.email} />
              <Row k="Subject" v={item.subject} />
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-2">Message</div>
                <div className="bg-[#060A14] border border-white/10 p-4 text-sm text-white/85 whitespace-pre-wrap leading-relaxed">
                  {item.message}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-white/5">
      <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">{k}</div>
      <div className="col-span-2 text-white/85 break-words">{v}</div>
    </div>
  );
}
